import { CONFIG } from '../config';

export type DinosaurSpecies =
  | 'raptor'
  | 'trex'
  | 'dilophosaurus'
  | 'spinosaurus'
  | 'compsognathus'
  | 'ankylosaurus';

export type DinosaurState =
  | 'wander'
  | 'notice'
  | 'charge'
  | 'stun'
  | 'reached_orb'
  | 'defeated';

export type Dinosaur = {
  id: string;
  species: DinosaurSpecies;
  state: DinosaurState;
  position: [number, number, number];
  velocity: [number, number, number];
  stunUntil: number;
  createdAt: number;
  scale: number;
  armorBroken: boolean;
};

export type ShockwaveType = 'wind' | 'impact' | 'shield';
export type Shockwave = {
  id: string;
  type: ShockwaveType;
  startedAt: number;
  durationMs: number;
  maxRadius: number;
  color: number;
  knockback: number;
};

export type GameState = {
  corruption: number;
  shieldActiveUntil: number;
  shieldLastTrigger: number;
  weaponCount: number;
  scoreRepel: number;
  elapsedMs: number;
  startedAt: number;
  isRespawning: boolean;
  respawnStartedAt: number;
  isMuted: boolean;
  totalDinosaurs: number;
  dinosaursDefeated: number;
  dinosaursRemaining: number;
  timeLimitMs: number;
  timeLeftMs: number;
  totalSpawned: number;
  isTimeUp: boolean;
  isRunComplete: boolean;
  isDead: boolean;
  deathStartedAt: number;
};

export type GameEvent =
  | 'blink_left'
  | 'blink_right'
  | 'tongue'
  | 'fist'
  | 'scissors'
  | 'open_palm'
  | 'face_lost'
  | 'face_found'
  | 'corruption_full'
  | 'spirit_respawn'
  | 'dino_defeated'
  | 'run_complete'
  | 'time_up'
  | 'player_died';

const initial = (): GameState => ({
  corruption: 0,
  shieldActiveUntil: 0,
  shieldLastTrigger: 0,
  weaponCount: 1,
  scoreRepel: 0,
  elapsedMs: 0,
  startedAt: 0,
  isRespawning: false,
  respawnStartedAt: 0,
  isMuted: false,
  totalDinosaurs: CONFIG.run.totalDinosaurs,
  dinosaursDefeated: 0,
  dinosaursRemaining: CONFIG.run.totalDinosaurs,
  timeLimitMs: CONFIG.run.timeLimitMs,
  timeLeftMs: CONFIG.run.timeLimitMs,
  totalSpawned: 0,
  isTimeUp: false,
  isRunComplete: false,
  isDead: false,
  deathStartedAt: 0,
});

export type StateListener = (state: GameState) => void;

export class GameStateStore {
  private state: GameState;
  private listeners: Set<StateListener> = new Set();

  constructor() {
    this.state = initial();
  }

  get(): GameState {
    return this.state;
  }

  set(patch: Partial<GameState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  startRun(): void {
    const now = performance.now();
    this.state = { ...initial(), startedAt: now };
    this.emit();
  }

  tickCorruption(deltaMs: number): void {
    if (this.state.isRespawning) return;
    let next = this.state.corruption;
    if (this.state.shieldActiveUntil > performance.now()) {
      next -= (CONFIG.shield.cleansePercent / (CONFIG.shield.durationMs / 1000)) * (deltaMs / 1000);
    }
    next = Math.max(0, Math.min(CONFIG.corruption.max, next));
    this.set({ corruption: next });
  }

  addCorruption(amount: number): void {
    if (
      this.state.isRespawning ||
      this.state.isTimeUp ||
      this.state.isRunComplete ||
      this.state.isDead
    ) {
      return;
    }
    const next = Math.min(CONFIG.corruption.max, this.state.corruption + amount);
    this.set({ corruption: next });
    if (next >= CONFIG.corruption.max) {
      this.beginDeath();
    }
  }

  beginDeath(): void {
    if (this.state.isDead || this.state.isTimeUp || this.state.isRunComplete) return;
    const now = performance.now();
    this.set({ isDead: true, deathStartedAt: now });
  }

  beginRespawn(): void {
    const now = performance.now();
    this.set({
      isRespawning: true,
      respawnStartedAt: now,
      shieldActiveUntil: 0,
    });
    setTimeout(() => {
      this.set({
        corruption: 0,
        isRespawning: false,
      });
    }, CONFIG.corruption.respawnDurationMs);
  }

  triggerShield(): void {
    if (this.state.isTimeUp || this.state.isRunComplete) return;
    const now = performance.now();
    this.set({
      shieldActiveUntil: now + CONFIG.shield.durationMs,
      shieldLastTrigger: now,
      corruption: Math.max(0, this.state.corruption - CONFIG.shield.cleansePercent),
    });
  }

  incRepelScore(n: number = 1): void {
    this.set({ scoreRepel: this.state.scoreRepel + n });
  }

  updateWeapons(): void {
    const corruptionRatio = this.state.corruption / CONFIG.corruption.max;
    let count: number;
    if (corruptionRatio < 0.3) count = 1;
    else if (corruptionRatio < 0.6) count = 3;
    else if (corruptionRatio < 0.9) count = 6;
    else count = 8;
    if (count !== this.state.weaponCount) this.set({ weaponCount: count });
  }

  tickElapsed(now: number): void {
    if (this.state.startedAt > 0) {
      this.set({ elapsedMs: now - this.state.startedAt });
    }
  }

  tickTimer(deltaMs: number): void {
    if (this.state.isTimeUp || this.state.isRunComplete) return;
    if (this.state.startedAt === 0) return;
    const next = Math.max(0, this.state.timeLeftMs - deltaMs);
    const updates: Partial<GameState> = { timeLeftMs: next };
    if (next <= 0) {
      updates.isTimeUp = true;
    }
    this.set(updates);
  }

  registerSpawn(): void {
    this.set({ totalSpawned: this.state.totalSpawned + 1 });
  }

  registerDefeat(): void {
    const defeated = this.state.dinosaursDefeated + 1;
    const winTarget = CONFIG.run.winDefeatCount;
    const remaining = Math.max(0, winTarget - defeated);
    const updates: Partial<GameState> = {
      dinosaursDefeated: defeated,
      dinosaursRemaining: remaining,
    };
    if (defeated >= winTarget) {
      updates.isRunComplete = true;
    }
    this.set(updates);
  }

  toggleMute(): void {
    this.set({ isMuted: !this.state.isMuted });
  }

  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const l of this.listeners) l(this.state);
  }
}

export const gameState = new GameStateStore();
